/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	BackSide,
	IcosahedronGeometry,
	Mesh,
	MeshBasicMaterial,
	MeshStandardMaterial,
} from 'three';

import { ARButton } from 'ratk';
import { GlobalComponent } from './global';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { System } from '@lastolivegames/becsy';

const CAMERA_ANGULAR_SPEED = Math.PI;

const VAMP_HOLE_MATERIAL = new MeshBasicMaterial({
	color: 0x000000,
});

export class InlineSystem extends System {
	constructor() {
		super();
		this.globalEntity = this.query((q) => q.current.with(GlobalComponent));
		this.needsSetup = true;
	}

	_loadModel(global) {
		const { camera, scene, renderer } = global;
		this.container = new Mesh(
			new IcosahedronGeometry(1, 1),
			new MeshStandardMaterial({ side: BackSide, color: 0x2e2e2e }),
		);
		scene.add(this.container);
		camera.position.set(0, 0.1, 0.3);
		global.gltfLoader.load('assets/gltf/sneaker.gltf', (gltf) => {
			const model = gltf.scene;
			this.container.add(model);
			model.name = 'mesh-prototype';

			gltf.scene.traverse((node) => {
				if (node && node.material) {
					let newMat = new MeshStandardMaterial({
						color: 0xffffff,
						map: node.material.map,
						normalMap: node.material.normalMap,
						roughness: 0.7,
					});

					if (node.material.name == 'default') {
						// newMat.transparent = true;
					}

					if (
						node.name === 'sockliner_logo_left' ||
						node.name === 'sockliner_logo_right' ||
						node.name == 'tongue_label_left' ||
						node.name == 'tongue_label_right' ||
						node.name == 'backtab_logo_left' ||
						node.name == 'backtab_logo_right' ||
						node.name == 'backtab_text_left' ||
						node.name == 'backtab_text_right'
					) {
						newMat.transparent = true;
					}

					if (
						node.name == 'sockliner_logo_left' ||
						node.name == 'tongue_label_left' ||
						node.name == 'backtab_logo_left' ||
						node.name == 'backtab_text_left' ||
						node.name == 'midsole_1_1'
					) {
						node.visible = true;
					} else if (
						node.name == 'sockliner_logo_right' ||
						node.name == 'tongue_label_right' ||
						node.name == 'backtab_logo_right' ||
						node.name == 'backtab_text_right' ||
						node.name == 'rt_midsole_accent'
					) {
						node.visible = false;
					}

					node.material = newMat;

					// console.log(node.name, node.material.name, node.visible);

					if (node.name == 'vholes') {
						node.material = VAMP_HOLE_MATERIAL;
						node.visible = true;
					}
				}
				if (node.geometry) {
					node.geometry.computeBoundsTree();
				}
			});
		});

		this.orbitControls = new OrbitControls(camera, renderer.domElement);
		this.orbitControls.target.set(0, 0.05, 0);
		this.orbitControls.update();
		this.orbitControls.enableZoom = true;
		this.orbitControls.enablePan = false;
		this.orbitControls.enableDamping = true;
		this.orbitControls.autoRotate = true;
		this.orbitControls.rotateSpeed *= -0.5;
		this.orbitControls.autoRotateSpeed = CAMERA_ANGULAR_SPEED;
		this.wasPresenting = false;
	}

	_setupButtons(global) {
		const arButton = document.getElementById('ar-button');
		const webLaunchButton = document.getElementById('web-launch-button');
		webLaunchButton.style.display = 'none';
		const { renderer } = global;
		ARButton.convertToARButton(arButton, renderer, {
			ENTER_XR_TEXT: 'Customize in MR',
			requiredFeatures: [
				'hit-test',
				'plane-detection',
				'mesh-detection',
				'anchors',
			],
			optionalFeatures: ['local-floor', 'bounded-floor', 'layers'],
			onUnsupported: () => {
				arButton.style.display = 'none';
				webLaunchButton.style.display = 'block';
			},
		});
		webLaunchButton.onclick = () => {
			window.open(
				'https://www.oculus.com/open_url/?url=' +
					encodeURIComponent(window.location.href),
			);
		};
		const genderSetting = document.getElementsByClassName('gender-options')[0];
		genderSetting.dataset.value = 'mens';
		const mensButton = document.getElementById('gender-mens');
		const womensButton = document.getElementById('gender-womens');
		mensButton.onclick = () => {
			genderSetting.dataset.value = 'mens';
			mensButton.classList.toggle('selected', true);
			womensButton.classList.toggle('selected', false);
		};
		womensButton.onclick = () => {
			genderSetting.dataset.value = 'womens';
			mensButton.classList.toggle('selected', false);
			womensButton.classList.toggle('selected', true);
		};

		const sizeOptions = document.getElementsByClassName('size-options')[0];
		const sizeButtons = new Array(...sizeOptions.getElementsByClassName('btn'));
		sizeButtons.forEach((button) => {
			button.onclick = () => {
				sizeButtons.forEach((button) => {
					button.classList.toggle('selected', false);
				});
				button.classList.toggle('selected', true);
				sizeOptions.dataset.value = button.innerHTML;
			};
		});
	}

	execute() {
		const global = this.globalEntity.current[0].read(GlobalComponent);

		if (this.needsSetup) {
			this._loadModel(global);
			this._setupButtons(global);
			this.needsSetup = false;
			return;
		}

		const isPresenting = global.renderer.xr.isPresenting;
		if (!this.wasPresenting && isPresenting) {
			this.container.visible = false;
			global.scene.traverse((object) => {
				if (object.userData.arOnly) {
					object.visible = true;
				}
			});
			const xrSession = global.renderer.xr.getSession();
			if (xrSession.updateTargetFrameRate) {
				xrSession.updateTargetFrameRate(72);
			}
		} else if (this.wasPresenting && !isPresenting) {
			this.container.visible = true;
			global.camera.position.set(0, 0.1, 0.4);
			global.scene.traverse((object) => {
				if (object.userData.arOnly) {
					object.visible = false;
				}
			});
		}

		if (this.container.visible) {
			this.orbitControls.update();
		}

		this.wasPresenting = isPresenting;
	}
}
